import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Phone } from "lucide-react";
import { HERO } from "@/constants/testIds";

const stats = [
  { v: "20+", l: "Years of senior banking expertise" },
  { v: "₹500Cr+", l: "Capital advised across mandates" },
  { v: "150+", l: "SME & mid-market engagements" },
  { v: "12", l: "Industries served" },
];

const WHATSAPP = "https://wa.me/919959133388?text=Hi%20Transformo%2C%20I%27d%20like%20to%20discuss%20a%20mandate.";
const TEL = "tel:+919959133388";

export const Hero = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      className="relative pt-10 md:pt-16 pb-24 md:pb-32 overflow-hidden bg-emerald-radial"
    >
      {/* Background: corporate skyline symbolising the transformation from company to corporation */}
      <div className="absolute inset-0 -z-0" aria-hidden>
        <img
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=80"
          alt=""
          className="w-full h-full object-cover opacity-[0.18]"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,28,20,0.55) 0%, rgba(10,28,20,0.85) 55%, #0A1C14 100%), radial-gradient(70% 60% at 25% 15%, rgba(16,185,129,0.22) 0%, transparent 65%)",
          }}
        />
      </div>
      <div className="grain absolute inset-0" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex flex-wrap items-center gap-2 text-[13px] md:text-base font-semibold tracking-[0.18em] uppercase text-brand-accent"
        >
          <span className="inline-block w-8 h-px bg-brand-accent/60" />
          Premium Investment Banking
          <span className="text-white/30">@</span>
          <span className="text-white">Vizag</span>
          <span className="text-white/40">—</span>
          <span className="italic font-serif normal-case tracking-normal text-white/90 text-base md:text-xl">
            the future city
          </span>
        </motion.p>

        <div className="mt-6 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="font-serif text-white text-[2.6rem] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
            >
              Transforming companies <br className="hidden md:block" />
              into <em className="not-italic text-brand-accent font-medium">corporations.</em>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.18 }}
              className="mt-7 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
            >
              Hyper-personal investment banking, financial advisory and business research for
              ambitious founders. Funding solutions, M&amp;A advisory and rigorous valuation —
              delivered with the discipline of a senior banker.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.32 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <button
                onClick={() => scrollTo("contact")}
                data-testid={HERO.ctaPrimary}
                className="btn-shine group inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-md px-6 py-3.5 text-sm font-semibold transition-colors"
              >
                Start a conversation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>

              <a
                href={WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HERO.ctaWhatsapp}
                className="inline-flex items-center gap-2 rounded-md border border-white/15 hover:border-brand-accent/60 text-white/90 hover:text-white px-5 py-3.5 text-sm font-medium transition-colors"
              >
                <MessageCircle className="w-4 h-4 text-brand-accent" />
                WhatsApp 9959133388
              </a>

              <a
                href={TEL}
                data-testid={HERO.ctaCall}
                className="inline-flex items-center gap-2 rounded-md text-white/70 hover:text-white px-3 py-3.5 text-sm transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call now
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-4"
          >
            <div className="glass rounded-md p-5">
              <p className="text-overline text-brand-accent">By the numbers</p>
              <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-5">
                {stats.map((s, i) => (
                  <div key={i}>
                    <p className="font-serif text-white text-3xl md:text-4xl tracking-tight">
                      {s.v}
                    </p>
                    <p className="mt-1 text-xs text-white/60 leading-snug">{s.l}</p>
                  </div>
                ))}
              </div>
              <div className="gold-line mt-5" />
              <p className="mt-4 text-xs text-white/55 italic font-serif">
                &ldquo;Capital is patient when the strategy is precise.&rdquo;
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -right-24 w-[560px] h-[560px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(closest-side, #10B981, transparent)" }}
      />
    </section>
  );
};

export default Hero;
